import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createDagFrameScheduler } from '../../Utils/dagPerformance';

export default function useLogicalGroupDragging({ disabled, zoom, onStart, onMove, onEnd }) {
  const drag = useRef(null);
  const callbacks = useRef({ onStart, onMove, onEnd });
  callbacks.current = { onStart, onMove, onEnd };
  const scheduler = useMemo(() => createDagFrameScheduler((delta) => callbacks.current.onMove?.(delta)), []);
  const end = useCallback((cancelled = false, event = null) => {
    const current = drag.current;
    if (!current) return;
    drag.current = null;
    scheduler.cancel();
    if (!cancelled && event) callbacks.current.onMove?.({
      x: (event.clientX - current.x) / current.zoom, y: (event.clientY - current.y) / current.zoom,
    });
    callbacks.current.onEnd?.(cancelled);
    if (current.element.hasPointerCapture?.(current.pointerId)) current.element.releasePointerCapture(current.pointerId);
  }, [scheduler]);
  useEffect(() => {
    if (disabled) end(true);
  }, [disabled, end]);
  useEffect(() => () => end(true), [end]);
  return {
    onPointerDown: (group, event) => {
      if (disabled || event.button !== 0 || event.target.closest('button')) return;
      event.stopPropagation();
      event.preventDefault();
      if (callbacks.current.onStart?.(group) === false) return;
      drag.current = { x: event.clientX, y: event.clientY, zoom: zoom?.() || 1,
        pointerId: event.pointerId, element: event.currentTarget };
      event.currentTarget.setPointerCapture?.(event.pointerId);
      event.currentTarget.focus({ preventScroll: true });
    },
    onPointerMove: (event) => {
      const current = drag.current;
      if (!current || event.pointerId !== current.pointerId) return;
      event.stopPropagation();
      scheduler.schedule({ x: (event.clientX - current.x) / current.zoom, y: (event.clientY - current.y) / current.zoom });
    },
    onPointerUp: (event) => {
      if (drag.current && event.pointerId === drag.current.pointerId) { event.stopPropagation(); end(false, event); }
    },
    onPointerCancel: (event) => { if (event.pointerId === drag.current?.pointerId) end(true); },
    onLostPointerCapture: () => end(true),
    onKeyDown: (event) => {
      if (event.key === 'Escape' && drag.current) { event.stopPropagation(); end(true); }
    },
  };
}
